import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useTranslation } from 'react-i18next'
import { getValueFromResource } from '../../../common/timeLiveFunction'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { MapContainer, TileLayer, Marker, Circle, Polygon, Popup, useMap, useMapEvents, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import Select from 'react-select'
import mapPreview from '../../../assets/image/map-preview.png'
import 'leaflet/dist/leaflet.css'
import './geofencing.css'
import { GetAccountGeoFenceProfileById, GetAccountGeoFenceZone, GetAccountGeoFenceZoneById, SystemGeoFenceBoundaryBehavior } from './GeofencingSlice'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import fetchTimeliveAPI from '../../../common/fetchTimeliveAPI'
import { ShowToast } from '../../../common/Toastr'
import Loader from '../../../common/Loader'
import infoSVG from "../../../assets/image/info-SVG.svg";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { TOOLTIP_STYLE } from "../../../common/TooltipStyle";

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
})

const BOUNDARY_TYPE_CIRCLE = 1
const BOUNDARY_TYPE_POLYGON = 2

interface PolygonPointOption {
    Latitude: number
    Longitude: number
    PointLabel?: string
    PointOrder?: number
}

interface ZoneOption {
    label: string
    value: number
    ZoneCode: string
    lat?: number
    lng?: number
    Radius?: number
    RadiusUnit?: string
    address?: string
    BoundaryType?: number
    PolygonPoints?: PolygonPointOption[] | null
}

interface ZoneFormValues {
    status: boolean
    ProfileName: string
    ProfileCode: string
    ProfileDescription: string
    GeoFenceBoundaryBehaviorId: number
}

interface SystemBehaviour {
    SystemGeoFenceBoundaryBehaviorId: number
    BoundaryBehaviorName: string
}

const MAP_TILE_MODES = [
    { label: 'Satellite', url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}' },
    { label: 'Street', url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}' },
    // { label: 'Terrain', url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}' },
]

const ZONE_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2']

const validationSchema = Yup.object({
    ProfileName: Yup.string().required('Profile Name is required'),
    ProfileCode: Yup.string().required('Profile Code is required'),
})

const zoneMarkerIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [0, -34],
})

const initialValues: ZoneFormValues = {
    status: false,
    ProfileName: '',
    ProfileCode: '',
    ProfileDescription: '',
    GeoFenceBoundaryBehaviorId: 0,
}

const isPolygonZone = (zone: ZoneOption) => zone.BoundaryType === BOUNDARY_TYPE_POLYGON && !!zone.PolygonPoints && zone.PolygonPoints.length > 0

const getSortedPolygonPositions = (zone: ZoneOption): [number, number][] => {
    if (!zone.PolygonPoints) return []
    return [...zone.PolygonPoints]
        .sort((a, b) => (a.PointOrder ?? 0) - (b.PointOrder ?? 0))
        .map(p => [p.Latitude, p.Longitude] as [number, number])
}

// const getZoneCenter = (zone: ZoneOption): [number, number] | null => {
//     if (isPolygonZone(zone)) {
//         const points = zone.PolygonPoints as PolygonPointOption[]
//         const avgLat = points.reduce((s, p) => s + p.Latitude, 0) / points.length
//         const avgLng = points.reduce((s, p) => s + p.Longitude, 0) / points.length
//         return [avgLat, avgLng]
//     }
//     if (zone.lat != null && zone.lng != null && (zone.lat !== 0 || zone.lng !== 0)) {
//         return [zone.lat, zone.lng]
//     }
//     return null
// }
const getZoneCenter = (zone: ZoneOption): [number, number] | null => {
    if (isPolygonZone(zone)) {
        const positions = getSortedPolygonPositions(zone)
        return positions.length > 0 ? positions[0] : null
    }
    if (zone.lat != null && zone.lng != null && (zone.lat !== 0 || zone.lng !== 0)) {
        return [zone.lat, zone.lng]
    }
    return null
}

// const getRadiusInFeet = (radius: number, unitLabel?: string) => {
//     const label = unitLabel?.toLowerCase();
//     if (label === 'miles' || label === 'mi') {
//         return radius * 5280; 
//     }
//     return radius;
// };
const getRadiusInFeet = (radius: number, unitLabel?: string) => {
    const label = unitLabel?.toLowerCase();
    if (label === 'miles' || label === 'mi') {
        return radius * 5280;
    }
    return radius;
};

const getPolygonAreaSqFt = (points: PolygonPointOption[]) => {
    if (!points || points.length < 3) return 0
    const R = 6378137
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const avgLatRad = toRad(points.reduce((s, p) => s + p.Latitude, 0) / points.length)
    const coords = points.map(p => ({
        x: toRad(p.Longitude) * R * Math.cos(avgLatRad),
        y: toRad(p.Latitude) * R,
    }))
    let area = 0
    for (let i = 0; i < coords.length; i++) {
        const j = (i + 1) % coords.length
        area += coords[i].x * coords[j].y
        area -= coords[j].x * coords[i].y
    }
    const areaSqM = Math.abs(area) / 2
    const SQ_FT_PER_SQ_M = 10.7639
    return areaSqM * SQ_FT_PER_SQ_M
}

const PolygonZone: React.FC<{ zone: ZoneOption, color: string, dispatch: any, t: any }> = ({ zone, color, dispatch, t }) => {
    const positions = getSortedPolygonPositions(zone)
    const firstPoint = positions[0]

    if (positions.length < 3 || !firstPoint) return null

    return (
        <>
            <Polygon
                positions={positions}
                pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.12,
                    weight: 2,
                    dashArray: '6 4',
                }}
            />

            <Marker position={firstPoint} icon={zoneMarkerIcon}>
                <Tooltip permanent direction="top" offset={[0, -6]} className="gf-zone-tooltip">
                    {zone.label}
                </Tooltip>
                <Popup autoClose closeOnClick>
                    <strong>{zone.label}</strong><br />
                    <em>{zone.ZoneCode}</em><br />
                    {zone.address}<br />
                    {getValueFromResource('Polygon Zone', dispatch, t)}
                </Popup>
            </Marker>
        </>
    )
}

const MapFitMulti: React.FC<{ zones: ZoneOption[] }> = ({ zones }) => {
    const map = useMap()
    useEffect(() => {
        if (zones.length === 0) return
        const timer = setTimeout(() => {
            try {
                map.invalidateSize()
                const bounds: [number, number][] = []
                zones.forEach(z => {
                    if (isPolygonZone(z)) {
                        // getSortedPolygonPositions(z).forEach(pos => bounds.push(pos))
                        getSortedPolygonPositions(z).filter(([lat, lng]) => !!lat && !!lng).forEach(pos => bounds.push(pos))
                        return
                    }
                    if (!z.lat || !z.lng) return
                    const radiusMeters = getRadiusInFeet(z.Radius ?? 500, z.RadiusUnit)
                    const deg = radiusMeters / 111320
                    bounds.push([z.lat - deg, z.lng - deg])
                    bounds.push([z.lat + deg, z.lng + deg])
                    // const lat = z.lat ?? 0
                    // const lng = z.lng ?? 0
                    // bounds.push([lat - deg, lng - deg])
                    // bounds.push([lat + deg, lng + deg])
                })
                if (bounds.length > 0) {
                    map.fitBounds(bounds as any, { padding: [40, 40], maxZoom: 17 })
                }
            } catch (e) { }
        }, 200)
        return () => {
            clearTimeout(timer)
            try { map.stop() } catch (e) { }
        }
    }, [zones, map])
    return null
}

const ProfileFormAndMap: React.FC = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { isLoading, pageNo, pageSize, startRec } = useAppSelector((state) => state.geofencing);
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const profileId = !!id;
    // const profileId = location?.pathname?.split('/')?.[2]
    const [mapTileIndex, setMapTileIndex] = useState(1)
    const [mapKey, setMapKey] = useState(0);
    const [selectedZones, setSelectedZones] = useState<ZoneOption[]>([])
    const [zonesList, setZonesList] = useState<ZoneOption[]>([])
    const [addZoneId, setAddZoneId] = useState<number | undefined>(undefined)
    const [systemBehaviours, setSystemBehaviours] = useState<SystemBehaviour[]>([])
    const [isLoadingMoreZones, setIsLoadingMoreZones] = useState(false);
    const [hasMoreZones, setHasMoreZones] = useState(true);
    const [isAddingZone, setIsAddingZone] = useState(false);
    const availableToAdd = zonesList.filter(z => !selectedZones.find(s => s.value === z.value));
    document.title = `Geofence Profile | Livetecs`;

    const getGeofenceZoneList = async (page: number = 1, append: boolean = false) => {
        try {
            if (append) {
                setIsLoadingMoreZones(true);
            }
            // const response = await dispatch(GetAccountGeoFenceZone({ pageNumber: page, pageSize: pageSize }))
            const response = await fetchTimeliveAPI(`api/GeoFenceZone/GetAccountGeoFenceZonesForDropdownAsync`, "GET", "", true, "0df3ab0c-4033-4013-8b1e-2f0b64f8cbab");
            const json = await response.json();
            if (json?.StatusCode === '200') {
                const data = json?.Data
                const options: ZoneOption[] = data?.map((item: any) => ({
                    label: item?.ZoneName,
                    value: item?.AccountGeoFenceZoneId,
                    ZoneCode: item?.ZoneCode,
                    lat: item?.Latitude ?? item?.lat ?? undefined,
                    lng: item?.Longitude ?? item?.lng ?? undefined,
                    Radius: item?.Radius ?? item?.Radius ?? 500,
                    RadiusUnit: item?.RadiusUnit ?? 'feet',
                    address: item?.Address ?? item?.StreetAddress ?? '',
                    BoundaryType: item?.BoundaryType ?? BOUNDARY_TYPE_CIRCLE,
                    PolygonPoints: item?.PolygonPoints ? typeof item.PolygonPoints === 'string' ? JSON.parse(item.PolygonPoints) : item.PolygonPoints : null,
                    // PolygonPoints: item?.PolygonPoints ?? null,
                }))
                if (append) {
                    setZonesList(prev => {
                        const existingIds = new Set(prev.map(x => x.value));
                        const newOptions = options.filter(x => !existingIds.has(x.value));
                        return [...prev, ...newOptions];
                    });
                } else {
                    setZonesList(options);
                }

                if (data.length < pageSize) {
                    setHasMoreZones(false);
                } else {
                    setHasMoreZones(true);
                }
                // setZonesList(options ?? [])
            }
        } catch (error) {
            console.log(error)
        } finally {

            setIsLoadingMoreZones(false);
        }
    }

    useEffect(() => {
        // getGeofenceZoneList()
        getGeofenceZoneList(1, false);
    }, [])

    const handleZoneMenuScrollToBottom = async () => {

        if (isLoadingMoreZones || !hasMoreZones) {
            return;
        }

        const nextPage = pageNo + 1;

        await getGeofenceZoneList(nextPage, true);
    };

    useEffect(() => {
        const getSystemGeoFenceBoundaryBehavior = async () => {
            try {
                const response = await dispatch(SystemGeoFenceBoundaryBehavior())
                if (response?.meta?.requestStatus === 'fulfilled') {
                    const data = response?.payload?.Data
                    setSystemBehaviours(data ?? [])
                    if (!profileId && data?.length > 0) {
                        const doNotAllow = data.find((item: any) => item.BoundaryBehaviorName?.trim().toLowerCase() === "don't allow")
                        formik.setFieldValue('GeoFenceBoundaryBehaviorId', doNotAllow?.SystemGeoFenceBoundaryBehaviorId ?? data[0].SystemGeoFenceBoundaryBehaviorId)
                        // formik.setFieldValue('GeoFenceBoundaryBehaviorId', data[0].SystemGeoFenceBoundaryBehaviorId)
                    }
                }
            } catch (error) {
                console.log(error)
            }
        }
        getSystemGeoFenceBoundaryBehavior()
    }, [])

    // useEffect(() => {
    //     if (availableToAdd.length > 0 && !addZoneId) {
    //         setAddZoneId(availableToAdd[0].value);
    //     }
    // }, [availableToAdd, addZoneId]);

    const getAccountGeoFenceZoneById = async () => {
        try {
            const response = await dispatch(GetAccountGeoFenceProfileById(id));
            if (response?.meta?.requestStatus === 'fulfilled') {
                const data = response?.payload?.Data[0];
                if (data) {
                    setFieldValue('ProfileName', data.ProfileName || '');
                    setFieldValue('ProfileCode', data.ProfileCode || '');
                    setFieldValue('GeoFenceBoundaryBehaviorId', Number(data.GeoFenceBoundaryBehaviorId) || 0);
                    setFieldValue('ProfileDescription', data.ProfileDescription || '');
                    setFieldValue('status', data.IsDisabled);
                    if (data.GeoFenceZonesList?.length > 0) {
                        const preSelected: ZoneOption[] = data.GeoFenceZonesList
                            .filter((z: any) => z.IsSelected)
                            .map((z: any) => ({
                                label: z.ZoneName,
                                value: z.AccountGeoFenceZoneId,
                                ZoneCode: z.ZoneCode,
                                lat: z.Latitude,
                                lng: z.Longitude,
                                Radius: z.Radius,
                                RadiusUnit: z.RadiusUnit,
                                address: z.StreetAddress,
                                BoundaryType: z.BoundaryType ?? BOUNDARY_TYPE_CIRCLE,
                                PolygonPoints: z.PolygonPoints ?? null,
                            }));
                        setSelectedZones(preSelected);
                        setMapKey(k => k + 1);
                    }

                    setTimeout(() => {
                        formik.validateForm();
                    }, 500);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (profileId) {
            getAccountGeoFenceZoneById()
        }
    }, [profileId])

    const handleAddZone = async () => {
        if (!addZoneId) return;
        const zone = zonesList.find(z => z.value === addZoneId)
        if (!zone) return;
        setIsAddingZone(true)
        let zoneToAdd: ZoneOption = zone
        try {
            const response = await dispatch(GetAccountGeoFenceZoneById(addZoneId))
            if (response?.meta?.requestStatus === 'fulfilled') {
                const d = response?.payload?.Data?.[0]
                if (d) {
                    zoneToAdd = {
                        ...zone,
                        lat: d.Latitude,
                        lng: d.Longitude,
                        Radius: d.Radius,
                        RadiusUnit: d.RadiusUnit,
                        address: d.StreetAddress ?? d.Address ?? zone.address,
                        BoundaryType: d.BoundaryType ?? zone.BoundaryType,
                        PolygonPoints: d.PolygonPoints ?? null,
                    }
                }
            }
        } catch (error) {
            console.log(error)
        }

        setSelectedZones(prev => [...prev, zoneToAdd])
        setAddZoneId(undefined)
        setMapKey(k => k + 1)
        setIsAddingZone(false)
    }

    const handleRemoveZone = (id: number) => {
        setSelectedZones(prev => prev.filter(z => z.value !== id))
        setMapKey(k => k + 1)
    }

    const formik = useFormik<ZoneFormValues>({
        initialValues,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const payload = {
                ...values,
                AccountGeoFenceZoneIds: selectedZones?.map((item) => item?.value).join(',')
            }
            let response: any;
            try {
                if (profileId) {
                    const updatePayload = {
                        ...values,
                        IsDisabled: values.status,
                        AccountGeoFenceProfileId: id,
                        GeoFenceZonesList: zonesList.map(zone => ({
                            AccountGeoFenceZoneId: zone.value,
                            IsSelected: selectedZones.some(selected => selected.value === zone.value),
                        })),
                    }
                    response = await fetchTimeliveAPI(`api/GeoFenceProfile/UpdateAccountGeoFenceProfile/${id}`, 'PUT', updatePayload, true);
                } else {
                    response = await fetchTimeliveAPI('api/GeoFenceProfile', 'POST', payload, true);
                }
                const json = await response.json();
                if (json?.StatusCode === '200' || json?.StatusCode == '201') {
                    ShowToast('Success', json.ResponseMessage, "success", "toast-top-right");
                    resetForm();
                    navigate('/geofenceProfile')
                } else {
                    ShowToast('error', json.ResponseMessage, "error", "toast-top-right")
                }
            } catch (error) {
                console.log(error);
            }
            setSubmitting(false)
        },
    })

    const getRadiusForMap = (radius: number, unitId: string) => {
        if (unitId?.toLowerCase() === 'feet' || unitId?.toLowerCase() === 'ft') {
            return radius * 0.3048;
        }

        if (unitId?.toLowerCase() === 'miles' || unitId?.toLowerCase() === 'mi') {
            return radius * 1609.344;
        }

        return radius;
    };
    // const radiusMetersForMapCircle = getRadiusForMap(rad, radiusUnit);

    const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, dirty } = formik

    const SQ_FEET_PER_SQ_MILE = 27_878_400
    const totalAreaFt2 = selectedZones.reduce((sum, z) => {
        if (isPolygonZone(z)) {
            return sum + getPolygonAreaSqFt(z.PolygonPoints as PolygonPointOption[])
        }
        const r = getRadiusInFeet(z.Radius ?? 0, z.RadiusUnit)
        return sum + Math.PI * r * r
    }, 0)

    const totalAreaMi2 = totalAreaFt2 / SQ_FEET_PER_SQ_MILE
    const totalAreaDisplay = totalAreaMi2 >= 1 ? `≈ ${totalAreaMi2.toFixed(3)} mi²` : `≈ ${totalAreaFt2.toFixed(0)} ft²`
    const canSave = isValid && dirty && selectedZones.length > 0
    const validCenterZone = selectedZones.find(z => getZoneCenter(z) != null)
    const firstZoneCenter = selectedZones.length > 0 ? getZoneCenter(selectedZones[0]) : null
    const hasMapData = firstZoneCenter != null
    const zonexName0 = selectedZones[0]?.label ?? ''

    return (
        <>
            {isLoading && <Loader />}
            <div className="card card-custom admin-card">
                <div className="card-header card-drop sticky-top bg-white fixed-top">
                    <h3 className="card-title fw-bold mainHeading">
                        {profileId ? getValueFromResource('Update Profile', dispatch, t) : getValueFromResource('Add Profile', dispatch, t)}
                    </h3>
                </div>
                <form onSubmit={formik.handleSubmit} noValidate className="gf-root my-3">
                    <div className="gf-left az-left-wide">
                        <div className="gf-left__head">
                            <h2 className="gf-left__title">
                                {getValueFromResource('Configure Profile', dispatch, t)}
                            </h2>
                            <p className="gf-left__sub">
                                {getValueFromResource('Set up your geographical boundary parameters', dispatch, t)}
                            </p>
                        </div>
                        {profileId && (
                            <>
                                <div className="gf-status-row">
                                    <span className=' form-label fs-6 fw-normal'>
                                        {getValueFromResource('Status', dispatch, t)}
                                    </span>
                                    <div className="gf-status-right">
                                        <div
                                            className={`gf-toggle-pill ${values.status ? 'gf-toggle-pill--off' : 'gf-toggle-pill--on'}`}
                                            onClick={() => setFieldValue('status', !values.status)}
                                        >
                                            <div className="gf-toggle-pill__thumb" />
                                        </div>
                                        <span className={values.status ? 'gf-badge-inactive' : 'gf-badge-active'}>
                                            {values.status ? getValueFromResource('InActive', dispatch, t) : getValueFromResource('Active', dispatch, t)}
                                        </span>
                                    </div>
                                </div>

                                <div className="gf-divider" />
                            </>
                        )}
                        <div className="gf-field">
                            <label className="required form-label fs-7 fw-normal">
                                {getValueFromResource('Profile Name', dispatch, t)}
                            </label>
                            <input
                                className="form-control fs-7 fw-normal"
                                autoComplete="off"
                                name="ProfileName"
                                value={values.ProfileName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {touched.ProfileName && errors.ProfileName && (
                                <span className="gf-error">{errors.ProfileName}</span>
                            )}
                        </div>

                        <div className={'gf-field'}>
                            <div className="gf-field">
                                <label className="required form-label fs-7 fw-normal">
                                    {getValueFromResource('Profile Code', dispatch, t)}
                                </label>
                                <input
                                    className="form-control fs-7 fw-normal"
                                    autoComplete="off"
                                    name="ProfileCode"
                                    value={values.ProfileCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                {touched.ProfileCode && errors.ProfileCode && (
                                    <span className="gf-error">{errors.ProfileCode}</span>
                                )}
                            </div>
                        </div>
                        <div className="gf-field">
                            <label className="form-label fs-7 fw-normal">
                                {getValueFromResource('Description', dispatch, t)}
                            </label>
                            <textarea
                                className="form-control fs-7 fw-normal"
                                autoComplete="off"
                                name="ProfileDescription"
                                value={values.ProfileDescription}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </div>

                        <div className="gf-separator">
                            <span>{getValueFromResource('Assign Zones', dispatch, t)}</span>
                        </div>

                        <div className="az-add-row">
                            <Select
                                className="az-zone-select"
                                classNamePrefix="az-zone-select"
                                placeholder="Select Zone(s)"
                                options={availableToAdd}
                                value={availableToAdd.find(opt => opt.value === addZoneId) || null}
                                onChange={(option) => setAddZoneId(option ? option.value : undefined)}
                                isClearable
                                menuPlacement="auto"
                                menuPosition="fixed"
                                menuPortalTarget={document.body}
                                onMenuScrollToBottom={handleZoneMenuScrollToBottom}
                                isLoading={isLoadingMoreZones}
                            />
                            <button
                                type="button"
                                className="az-add-btn"
                                onClick={handleAddZone}
                                disabled={!addZoneId || isAddingZone}
                            >
                                + Add Zone
                            </button>
                        </div>

                        <div className="az-table-wrap">
                            <table className="az-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '20%' }}>{getValueFromResource('Zone Name', dispatch, t)}</th>
                                        <th style={{ width: '20%' }}>{getValueFromResource('Zone Code', dispatch, t)}</th>
                                        <th style={{ width: '15%' }}>{getValueFromResource('Radius', dispatch, t)}</th>
                                        <th style={{ width: '35%' }}>{getValueFromResource('Address', dispatch, t)}</th>
                                        <th style={{ width: 36 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedZones.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="az-table-empty">
                                                {getValueFromResource('No zones assigned. Use the dropdown above to add zones.', dispatch, t)}
                                            </td>
                                        </tr>
                                    ) : (
                                        selectedZones.map(z => (
                                            <tr key={z.value}>
                                                <td className="az-td-name">{z.label}</td>
                                                <td><span className="az-code-pill">{z.ZoneCode}</span></td>
                                                <td>
                                                    {isPolygonZone(z) ? (
                                                        <span className="az-radius-pill">—</span>
                                                    ) : (
                                                        <span className="az-radius-pill">{z.Radius ?? '—'}{z.RadiusUnit?.toLowerCase() === 'feet' ? ' ft' : z.RadiusUnit?.toLowerCase() === 'miles' ? ' mi' : ''}</span>
                                                    )}
                                                </td>
                                                <td className="az-td-addr">{z.address ?? '—'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="az-remove-btn"
                                                        onClick={() => handleRemoveZone(z.value)}
                                                        title="Remove zone"
                                                    >
                                                        ✕
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <h2 className="gf-preview-title d-flex align-items-center gap-1">
                            {getValueFromResource('Outside Geofence Behavior', dispatch, t)}
                            <div
                                style={{ marginBottom: "2px" }}
                                data-tooltip-id="tooltip-description"
                                data-tooltip-content={getValueFromResource('Define how the system should respond when an employee attempts to mark attendance from a location that does not fall within any geofence zone assigned to the selected profile. Choose whether to Allow Freely, Allow with Warning, or Do Not Allow the attendance action.', dispatch, t)}
                            >
                                <img src={infoSVG} alt="" width={12} height={12} className="tooltip-info-icon" />
                            </div>
                        </h2>
                        <div className="az-radio-row">
                            {systemBehaviours.map((val, index) => (
                                <label
                                    key={index}
                                    className="d-flex align-items-center gap-2 form-check-label"
                                >
                                    <input
                                        type="radio"
                                        name="GeoFenceBoundaryBehaviorId"
                                        className="form-check-input m-0"
                                        value={val?.SystemGeoFenceBoundaryBehaviorId}
                                        checked={values.GeoFenceBoundaryBehaviorId === val?.SystemGeoFenceBoundaryBehaviorId}
                                        onChange={() => setFieldValue('GeoFenceBoundaryBehaviorId', val.SystemGeoFenceBoundaryBehaviorId)}
                                    />
                                    {getValueFromResource(`${val?.BoundaryBehaviorName}`, dispatch, t)}
                                </label>
                            ))}
                        </div>

                        <div className="gf-divider" />

                        <div className="gf-footer">
                            <button
                                type="submit"
                                className="gf-btn-save"
                                disabled={isSubmitting || !canSave}
                            >
                                {profileId ? getValueFromResource('Update Profile', dispatch, t) : getValueFromResource('Create Profile', dispatch, t)}
                            </button>
                        </div>

                    </div>

                    <div className="az-right">

                        <div className="gf-preview-header">
                            <h2 className="gf-preview-title">
                                {getValueFromResource('Zones Preview', dispatch, t)}
                            </h2>
                            <div className="gf-map-tabs">
                                {MAP_TILE_MODES.map((mode, idx) => (
                                    <button
                                        key={mode.label}
                                        type="button"
                                        className={`gf-map-tab${mapTileIndex === idx ? ' gf-map-tab--active' : ''}`}
                                        onClick={() => { setMapTileIndex(idx); setMapKey(k => k + 1) }}
                                    >
                                        {getValueFromResource(mode.label, dispatch, t)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="gf-map-area">
                            {hasMapData ? (
                                <div className="gf-map-wrapper">
                                    <MapContainer
                                        key={mapKey}
                                        center={firstZoneCenter as [number, number]}
                                        zoom={12}
                                        style={{ width: '100%', height: '100%' }}
                                        scrollWheelZoom
                                        zoomControl={true}
                                        attributionControl={false}
                                        zoomAnimation={false}
                                        fadeAnimation={false}
                                    >
                                        <TileLayer url={MAP_TILE_MODES[mapTileIndex].url} maxZoom={19} />
                                        <MapFitMulti zones={selectedZones} />
                                        {selectedZones.map((z, i) => {
                                            const color = ZONE_COLORS[i % ZONE_COLORS.length]
                                            if (isPolygonZone(z)) {
                                                return (
                                                    <PolygonZone
                                                        key={z.value}
                                                        zone={z}
                                                        color={color}
                                                        dispatch={dispatch}
                                                        t={t}
                                                    />
                                                )
                                            }

                                            if (!z.lat || !z.lng) return null
                                            return (
                                                <React.Fragment key={z.value}>
                                                    <Circle
                                                        center={[z.lat, z.lng]}
                                                        radius={getRadiusForMap(z.Radius ?? 500, z.RadiusUnit ?? 'feet')}
                                                        pathOptions={{
                                                            color,
                                                            fillColor: color,
                                                            fillOpacity: 0.12,
                                                            weight: 2,
                                                            dashArray: '6 4',
                                                        }}
                                                    />
                                                    <Marker position={[z.lat, z.lng]} icon={zoneMarkerIcon}>
                                                        <Tooltip permanent direction="top" offset={[0, -6]} className="gf-zone-tooltip">
                                                            {z.label || z.ZoneCode || 'Zone'}
                                                        </Tooltip>
                                                        <Popup>
                                                            <strong>{z.label}</strong><br />
                                                            <em>{z.ZoneCode}</em><br />
                                                            {z.address}<br />
                                                            Radius: {z.Radius ?? 500} {z.RadiusUnit}
                                                        </Popup>
                                                    </Marker>
                                                </React.Fragment>
                                            )
                                        })}
                                    </MapContainer>

                                    <div className="gf-map-name-badge">
                                        <span className="gf-badge-dot" />
                                        {selectedZones.length === 1 ? (zonexName0.length > 28 ? zonexName0.substring(0, 26) + '…' : zonexName0)
                                            : `${selectedZones.length} zones assigned`}
                                    </div>
                                </div>
                            ) : (
                                <div className="gf-map-placeholder">
                                    <img src={mapPreview} width={44} height={44} alt="map preview" />
                                    <p className="gf-placeholder-title">
                                        {getValueFromResource('Zone Preview', dispatch, t)}
                                    </p>
                                    <p className="gf-placeholder-sub">
                                        {getValueFromResource('Select zones from the dropdown to see them on the map.', dispatch, t)}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="gf-stats-bar">
                            <div className="gf-stat">
                                <span className="gf-stat__lbl">{getValueFromResource('Total Zones', dispatch, t)}</span>
                                <span className="gf-stat__val">{selectedZones.length > 0 ? selectedZones.length : '—'}</span>
                            </div>
                            <div className="gf-stat">
                                <span className="gf-stat__lbl">{getValueFromResource('Total Area', dispatch, t)}</span>
                                <span className="gf-stat__val">{selectedZones.length > 0 ? totalAreaDisplay : '—'}</span>
                            </div>
                            <div className="gf-stat">
                                <span className="gf-stat__lbl">{getValueFromResource('Boundary', dispatch, t)}</span>
                                <span className="gf-stat__val">
                                    {systemBehaviours?.find(item => item.SystemGeoFenceBoundaryBehaviorId === values.GeoFenceBoundaryBehaviorId)?.BoundaryBehaviorName ?? '—'}
                                </span>
                            </div>
                            <div className="gf-stat">
                                <span className="gf-stat__lbl">{getValueFromResource('Status', dispatch, t)}</span>
                                <span className={`gf-stat__val ${values.status ? 'gf-stat__val--inactive' : 'gf-stat__val--active'}`}>
                                    {values.status ? getValueFromResource('InActive', dispatch, t) : getValueFromResource('Active', dispatch, t)}
                                </span>
                            </div>
                        </div>

                    </div>

                </form>
            </div>
            <ReactTooltip
                id="tooltip-description"
                style={TOOLTIP_STYLE}
                place="top"
                delayShow={100}
                delayHide={100}
                clickable
                render={({ content }) => {
                    const isLongContent = content && content?.length > 90;
                    return (
                        <div style={{ maxWidth: isLongContent ? '380px' : '220px' }}>
                            {content}
                        </div>
                    );
                }}
            />
        </>
    )
}

export default ProfileFormAndMap